import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import type { components } from "@contracts/generated/editor-service";

import { useApiClient } from "./ApiContext";
import { unwrap } from "./client";
import { queryKeys } from "./hooks";
import type { PackFileContent, PackFileList } from "./packFiles";

/**
 * The Projects page's Tenants view (#1165). Read-only: what each preview target
 * runs now (asked of its runtime on every read), its picked Project, and the
 * pack files it runs. A target that does not answer is a row with `live: null`,
 * never a failed list.
 */
export type TenantList = components["schemas"]["TenantList"];
export type TenantSummary = components["schemas"]["TenantSummary"];
export type TenantProject = components["schemas"]["TenantProject"];

/**
 * The Tenants tab's "Runtime per environment" (#1173): what each tenant is
 * declared to run in Dev, Staging and Prod, read from git at each repository's
 * main branch, and where an environment differs.
 */
export type TenantEnvironments = components["schemas"]["TenantEnvironments"];
export type TenantEnvironmentRow =
  components["schemas"]["TenantEnvironmentItem"];
export type TenantEnvironmentCell = components["schemas"]["TenantEnvironment"];
export type TenantEnvironmentPin = components["schemas"]["PackPin"];

export function useTenantEnvironmentsQuery(): UseQueryResult<TenantEnvironments> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.tenantEnvironments,
    async queryFn({ signal }) {
      return unwrap(await client.GET("/v1/tenants/environments", { signal }));
    },
  });
}

export function useTenantsQuery(): UseQueryResult<TenantList> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.tenants,
    async queryFn({ signal }) {
      return unwrap(await client.GET("/v1/tenants", { signal }));
    },
  });
}

export function useTenantPackFilesQuery(
  tenantId: string,
): UseQueryResult<PackFileList> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.tenantPackFiles(tenantId),
    async queryFn({ signal }) {
      return unwrap(
        await client.GET("/v1/tenants/{previewTenantId}/files", {
          params: { path: { previewTenantId: tenantId } },
          signal,
        }),
      );
    },
    enabled: tenantId.length > 0,
  });
}

export function useTenantPackFileQuery(
  tenantId: string,
  path: string | null,
): UseQueryResult<PackFileContent> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.tenantPackFile(tenantId, path ?? ""),
    async queryFn({ signal }) {
      return unwrap(
        await client.GET("/v1/tenants/{previewTenantId}/files/content", {
          params: {
            path: { previewTenantId: tenantId },
            query: { path: path ?? "" },
          },
          signal,
        }),
      );
    },
    enabled: tenantId.length > 0 && path !== null,
  });
}
